import { useEffect, useState } from "react";
import { api, money, money4, KIND_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Gauge, Loader2, Save, AlertTriangle, DollarSign, Activity, Cpu, SlidersHorizontal, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const WEIGHT_LABELS = {
  emotional_pull: { label: "Emotional pull", hint: "How strongly the concept resonates (remembrance, love, pride)." },
  giftability: { label: "Giftability", hint: "Likelihood it's bought as a gift for someone else." },
  occasion_demand: { label: "Occasion demand", hint: "Tied to a holiday, event or recurring moment." },
  competition_opportunity: { label: "Competition opportunity", hint: "Room to win vs saturated listings (higher = less crowded)." },
  margin_score: { label: "Margin", hint: "Expected profit margin on the product." },
  seasonality_fit: { label: "Seasonality fit", hint: "Timeliness against the current season/calendar." },
};

export default function Admin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limitInput, setLimitInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] = useState(null);
  const [defaultWeights, setDefaultWeights] = useState(null);
  const [savingWeights, setSavingWeights] = useState(false);

  const load = () => api.get("/admin/usage").then(({ data }) => {
    setData(data);
    setLimitInput(data.monthly_cost_limit != null ? String(data.monthly_cost_limit) : "");
  }).finally(() => setLoading(false));

  const loadWeights = () => api.get("/admin/score-weights").then(({ data }) => {
    setWeights(data.weights);
    setDefaultWeights(data.default);
  });

  useEffect(() => { load(); loadWeights(); }, []);

  const saveLimit = async () => {
    setSaving(true);
    try {
      const val = limitInput.trim() === "" ? null : Number(limitInput);
      await api.put("/admin/settings", { monthly_cost_limit: val });
      toast.success(val == null ? "Cost limit removed" : `Monthly limit set to ${money(val)}`);
      load();
    } finally { setSaving(false); }
  };

  const weightTotal = weights ? Object.values(weights).reduce((a, b) => a + Number(b || 0), 0) : 0;
  const normPct = (v) => (weightTotal > 0 ? Math.round((Number(v || 0) / weightTotal) * 100) : 0);

  const setWeight = (k, v) => setWeights((w) => ({ ...w, [k]: v }));

  const saveWeights = async () => {
    setSavingWeights(true);
    try {
      const { data } = await api.put("/admin/score-weights", { weights });
      setWeights(data.weights);
      toast.success("Scoring weights updated — new opportunity scores will use them.");
    } finally { setSavingWeights(false); }
  };

  const resetWeights = () => {
    if (defaultWeights) setWeights({ ...defaultWeights });
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const pct = data.budget_used_pct;
  const overBudget = pct != null && pct >= 100;
  const chartData = (data.by_day || []).map((d) => ({ day: d.day.slice(5), cost: Number(d.cost.toFixed(4)) }));

  return (
    <div className="max-w-5xl mx-auto" data-testid="admin-page">
      <div className="mb-6">
        <p className="overline text-blue-600">Admin · AI Cost Controls</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Usage &amp; spend</h1>
        <p className="text-sm text-zinc-500 mt-1">Estimated AI cost this month. <span className="text-zinc-400">Figures are estimates based on token usage.</span></p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {/* Spend card */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <div className="flex items-center gap-2 text-zinc-400"><DollarSign className="w-4 h-4" /><span className="overline">Spent this month</span></div>
          <div className="font-display text-4xl font-bold tracking-tight mt-2">{money(data.month_spend)}</div>
          {data.monthly_cost_limit != null ? (
            <>
              <div className="text-xs text-zinc-500 mt-1">of {money(data.monthly_cost_limit)} limit</div>
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden mt-3">
                <div className={`h-full transition-all ${overBudget ? "bg-rose-500" : pct > 75 ? "bg-amber-500" : "bg-blue-600"}`} style={{ width: `${Math.min(pct || 0, 100)}%` }} />
              </div>
              {overBudget && <div className="flex items-center gap-1.5 text-xs text-rose-600 mt-2"><AlertTriangle className="w-3.5 h-3.5" /> Limit reached — AI generation is blocked.</div>}
            </>
          ) : <div className="text-xs text-zinc-400 mt-1">No limit set</div>}
        </div>

        {/* Calls card */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <div className="flex items-center gap-2 text-zinc-400"><Activity className="w-4 h-4" /><span className="overline">Total AI calls</span></div>
          <div className="font-display text-4xl font-bold tracking-tight mt-2">{data.total_calls}</div>
          <div className="text-xs text-zinc-400 mt-1">across all features</div>
        </div>

        {/* Limit setter */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <div className="flex items-center gap-2 text-zinc-400"><Gauge className="w-4 h-4" /><span className="overline">Monthly cost limit</span></div>
          <div className="flex gap-2 mt-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
              <Input type="number" min="0" step="1" value={limitInput} onChange={(e) => setLimitInput(e.target.value)} placeholder="No limit" className="pl-7" data-testid="limit-input" />
            </div>
            <button onClick={saveLimit} disabled={saving} data-testid="save-limit-btn" className="h-10 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[11px] text-zinc-400 mt-2">Leave empty for no limit. When reached, all AI generation is blocked until raised.</p>
        </div>
      </div>

      {/* Daily spend chart */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-4">
        <h2 className="font-display text-base font-bold tracking-tight flex items-center gap-2 mb-4"><Cpu className="w-4 h-4 text-zinc-400" /> Daily estimated spend</h2>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money4(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
              <Bar dataKey="cost" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="h-[200px] flex items-center justify-center text-sm text-zinc-400">No usage yet.</div>}
      </div>

      {/* By-kind breakdown */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <h2 className="font-display text-base font-bold tracking-tight mb-4">Spend by feature</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-400 border-b border-zinc-200">
              <th className="pb-2 overline font-medium">Feature</th>
              <th className="pb-2 overline font-medium text-right">Calls</th>
              <th className="pb-2 overline font-medium text-right">Tokens (in/out)</th>
              <th className="pb-2 overline font-medium text-right">Images</th>
              <th className="pb-2 overline font-medium text-right">Est. cost</th>
            </tr>
          </thead>
          <tbody>
            {(data.by_kind || []).map((k) => (
              <tr key={k.kind} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="py-2.5 font-medium">{KIND_LABELS[k.kind] || k.kind}</td>
                <td className="py-2.5 text-right text-zinc-600">{k.calls}</td>
                <td className="py-2.5 text-right text-zinc-500 font-mono text-xs">{k.tokens_in.toLocaleString()}/{k.tokens_out.toLocaleString()}</td>
                <td className="py-2.5 text-right text-zinc-600">{k.images || 0}</td>
                <td className="py-2.5 text-right font-bold text-blue-600">{money4(k.cost)}</td>
              </tr>
            ))}
            {(!data.by_kind || !data.by_kind.length) && <tr><td colSpan={5} className="py-8 text-center text-zinc-400">No usage yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Scoring rubric weights */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mt-4" data-testid="score-weights-section">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="font-display text-base font-bold tracking-tight flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-zinc-400" /> Scoring rubric weights</h2>
            <p className="text-sm text-zinc-500 mt-1">Tune how much each dimension influences the opportunity score. Weights are auto-normalized, so only the relative balance matters.</p>
          </div>
          <span className="shrink-0 text-xs font-medium px-2 py-1 rounded-md bg-zinc-100 text-zinc-500">Applies to new scores</span>
        </div>

        {!weights ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
        ) : (
          <>
            <div className="mt-4 space-y-4">
              {Object.keys(WEIGHT_LABELS).map((k) => (
                <div key={k} data-testid={`weight-row-${k}`}>
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div>
                      <span className="text-sm font-medium">{WEIGHT_LABELS[k].label}</span>
                      <span className="text-xs text-zinc-400 ml-2">{WEIGHT_LABELS[k].hint}</span>
                    </div>
                    <span className="shrink-0 text-sm font-bold text-blue-600 tabular-nums w-12 text-right" data-testid={`weight-pct-${k}`}>{normPct(weights[k])}%</span>
                  </div>
                  <input
                    type="range" min="0" max="1" step="0.01"
                    value={Number(weights[k] || 0)}
                    onChange={(e) => setWeight(k, Number(e.target.value))}
                    className="w-full accent-blue-600 cursor-pointer"
                    data-testid={`weight-slider-${k}`}
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-zinc-100">
              <button onClick={resetWeights} data-testid="reset-weights-btn" className="h-10 px-3 rounded-md border border-zinc-200 hover:bg-zinc-50 text-zinc-600 text-sm font-medium flex items-center gap-1.5">
                <RotateCcw className="w-4 h-4" /> Reset to default
              </button>
              <button onClick={saveWeights} disabled={savingWeights} data-testid="save-weights-btn" className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1.5">
                {savingWeights ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save weights
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
